
import { useEffect, useRef, useState, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography } from "@mui/material";
//import { differenceWith, isEqual } from "lodash";
import { RecipientTypeEnum } from "../../common/enums/RecipientTypeEnum";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { DialogResult, DialogState, BindDialogReturn, useDialogState, bindDialog, DialogProps } from "../../hooks/use-dialog-state/UseDialogState";
import FormInput from "../forms/form-input/FormInput";
import Form from "../forms/form/Form";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import NotificationListEditorUtils, { NotificationListAdminMode, NotificationListEditorDialogInitValues, NotificationListEditorDialogResultValues, NotificationListFormType, defaultFormValues, radioButtons } from "./NotificationListEditorDialogUtils";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useNotificationListReferenceData } from "../../queries/notification-lists/UseNotificationListReferenceData";
import { useCustomerAttribute } from "../../queries/attributes/UseCustomerAttribute";
import { useGetNotificationList } from "../../queries/notification-lists/UseGetNotificationList";
import LoadingZone from "../loading-zone/LoadingZone";
import FormProperty from "../forms/form-property/FormProperty";
import FormRecipientList, { FormRecipientListType } from "../form-recipient-list/FormRecipientList";
import { useNotificationListEditorState } from "./NotificationListEditorState";
import FullScreenDialog, { FullScreenDialogCloseReason } from "../full-screen-dialog/FullScreenDialog";
import SettingsBar, { SettingsActionEnum } from "../settings-bar/SettingsBar";
import SideSheet from "../side-sheet/side-sheet/SideSheet";
import OrganizationPicker from "../organization-picker/OrganizationPicker";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import SideSheetRadioGroup from "../side-sheet/side-sheet-radio-group/SideSheetRadioGroup";
import SideSheetSwitch from "../side-sheet/side-sheet-switch/SideSheetSwitch";
import { IOrganization } from "../../services/organization-data-service/Models";
import SideSheetFilterInput from "../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import { isMobile, isTablet } from "react-device-detect";
import { ContextActionMenu } from "../contextual-app-bar/ContextActionMenu";
import { FormRecipientActionItem, FormRecipientActions, recipientActionItems } from "../form-recipient-list/FormRecipientListUtils";
import { useSaveNotificationList } from "../../queries/notification-lists/UseSaveNotificationList";
import NotificationListEditorDialogUtils from "./NotificationListEditorDialogUtils";
import { ActionMenuItem } from "../action-menu/ActionMenuItem";

export type NotificationListEditorDialogResultReason = FullScreenDialogCloseReason | "cancel" | "save";
export type NotificationListEditorDialogResult = DialogResult<NotificationListEditorDialogResultValues, NotificationListEditorDialogResultReason>;
export type NotificationListEditorDialogProps = DialogProps<NotificationListEditorDialogInitValues, NotificationListEditorDialogResult>;

export default function NotificationListEditorDialog({
    open,
    onClose,
    values,
}: NotificationListEditorDialogProps): JSX.Element {

    const isPhone = isMobile && !isTablet;
    const [openSideSheet, setOpenSideSheet] = useState(isPhone ? false: true);
    const { t } = useTranslation();
    const { acknowledge, confirm } = useConfirmation();
    
    const mode = (values?.notificationListID ?? 0) <= 0  ? 
    NotificationListAdminMode.Add : 
    NotificationListAdminMode.Edit;

    const recipientList = useRef<FormRecipientListType>(undefined);

    const {
        state,
        setViewSelected,
        setRecipientFilterText,
        setRecipientType,
        setOrganization,
        resetState
    } = useNotificationListEditorState();

    const {data, status } = useQueryNotifications(useGetNotificationList)({
        groupID: values?.groupID as number,
        organizationID: values?.organizationID as number,
        notificationListID: values?.notificationListID as number,
        enabled: values?.groupID !== undefined
    });

    const {data: allowVoiceAlarmAttribute } = useQueryNotifications(useCustomerAttribute)({
        groupID: values?.groupID ?? -1 ,
        name: 'allow voice alarm',
        enabled: values?.groupID !== undefined
    });

    const allowVoiceAlarm = allowVoiceAlarmAttribute?.attributeValue.toLowerCase() === 'true';

    const {data: referenceData, status: referenceStatus, refetch } = useQueryNotifications(useNotificationListReferenceData)({
        groupID: values?.groupID ?? -1,
        organizationID: values?.organizationID ?? -1,
        enabled: values?.groupID !== undefined
    });

    const { mutate: saveNotificationList/*, status*/ } = useQueryNotifications(useSaveNotificationList)({
        groupID: values?.groupID as number,
        organizationID: values?.organizationID as number,
        onSuccess: async (data) => {
            acknowledge(t(mode === NotificationListAdminMode.Add ? 
                            "notificationList.successMessageAdded" : 
                            "notificationList.successMessageUpdated", { 
                                name: data.name
                            }
                        ));

            await refetch();                        
            
            onClose({
                reason: "save",
                values: {
                    notificationList: data
                }
            });
        }
    });

    const form = useForm<NotificationListFormType>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const {
        //setValue,
        handleSubmit,
        formState: {
            isDirty,
            isValid,
            isSubmitting,
            //dirtyFields
        },
        reset,
        watch,
        //trigger
    } = form;

    const [
        userRecipients, 
    ] = watch([
        "userRecipients"
    ]);

    const isLoading= status === 'loading' || referenceStatus === 'loading';
    const selectedRecipientCount = userRecipients.flatMap(o => o.recipients).filter(o => o.selected).length;
    const enableViewSelected = selectedRecipientCount > 0;

    async function onOkay(data: NotificationListFormType) {

        const model = NotificationListEditorDialogUtils.mapToModel(data);

        saveNotificationList(model);

        reset();

        resetState();
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getCancelConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ reason: "cancel" });
            reset();
            resetState();
        }
    }

    async function getCancelConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t(mode === NotificationListAdminMode.Add ? "notificationList.cancelAdd" : "notificationList.cancelEdit"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }


    function onViewSelected(value: boolean) {
        setViewSelected(value);
    }

    function onSetFilter(value: string) {
        setRecipientFilterText(value);
    }

    function onRecipientTypeChanged(value: RecipientTypeEnum) {
        setRecipientType(value);
    }

    function onOrganizationChanged(item: IOrganization) {
        setOrganization(item.id);
    }

    function onExecuteAction(action: ActionMenuItem) {

        const actionItem = action as FormRecipientActionItem;

        switch(actionItem.action) {
            case FormRecipientActions.Add:
                recipientList?.current?.executeAction(actionItem.action);
                break;
        }

    }

    const _validateName = (value: string): boolean => {
        return value.toString().trim().length <= 50;
    }

    useEffect(() => {
        if (open) {
            if (data && referenceData) {
                reset(NotificationListEditorUtils.mapToForm(data, referenceData, allowVoiceAlarm));
            }
        } else {
            reset(defaultFormValues);
        }
    }, [
        reset, 
        data,
        referenceData,
        allowVoiceAlarm,
        open
    ]);

    useEffect(() => {
        if (open && values?.organizationID) {
            setOrganization(values.organizationID);
        }
    }, [
        values,
        open,
        setOrganization
    ]);

    useEffect(() => {
        if (open && values?.recipientType) {
            setRecipientType(values.recipientType);
        }

    }, [
        open,
        values,
        setRecipientType
    ]);

    return (
        <>
            <FullScreenDialog open={open} onClose={reason => onClose({ reason: reason })}>
                <SettingsBar title={mode === NotificationListAdminMode.Add ? 'New Notification List': 'Edit Notification List'} 
                            onClose={
                                async (reason: SettingsActionEnum) => {
                                    reason === SettingsActionEnum.OK
                                        ? handleSubmit(async (data) => onOkay(data))()
                                        : onCancel();
                                }}
                            modified={isDirty && isValid && !isSubmitting}
                 />

                <LoadingZone isLoading={isLoading}>
                    <Form form={form}>


                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}>
                            <ContextActionMenu 
                                viewOptionState={state.viewOptionState}
                                actions={recipientActionItems}
                                onExecuteAction={onExecuteAction}
                                hideOptionsAction={!isPhone}
                                onOverrideViewOptionsClick={() => {setOpenSideSheet(!openSideSheet)}}
                                buttonColor="default"
                                hideAddToFavoritesAction={true}
                                />
                        </Box>

                        <Stack
                            direction='row'
                            sx={{
                                display: 'flex',
                                flex: '1 1 auto',
                                overflow: 'hidden'
                            }}>                        


                            <Stack
                                direction='column'
                                sx={{
                                    display: 'flex',
                                    flex: '1 1 auto',
                                    overflow: 'hidden'
                                }}>
                                <FormProperty>
                                    <FormInput
                                        name="name"
                                        label={t('properties.name')}
                                        sx={{
                                            width: 1,
                                            marginTop: 1,
                                            marginBottom: 1,
                                        }}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: t('notificationList.nameRequired')
                                            },
                                            validate: (value: string) => !_validateName(value) ?
                                            t('notificationList.nameLength') : 
                                            true
                                        }}

                                    />
                                </FormProperty>


                                <Typography variant="subtitle1" sx={{
                                    fontWeight: 600
                                }} >
                                    Recipients
                                </Typography>



                            <FormRecipientList 
                                visible={open && !isLoading}
                                ref={recipientList}
                                filterText={state.recipientFilterText}
                                recipientType={state.recipientType}
                                viewSelected={state.viewSelected}
                                organizationID={state.organizationID === -1 ? undefined : state.organizationID} 
                                allowVoiceAlarm={allowVoiceAlarm} 
                                hideActions={true}
                            />

                            </Stack>



                            <SideSheet
                                    title={t("options")}
                                    open={openSideSheet ?? false}
                                    hideCloseButton={!isPhone}
                                    //onClose={() => onCloseSideSheet && onCloseSideSheet()}
                                    onClose={() => setOpenSideSheet(false)}
                                    onReset={resetState}
                                    >


                                        <OrganizationPicker 
                                            label={getObjectTypeName(eLynxObjectTypeEnum.Organization)}
                                            color="inherit"
                                            //organizationID={state.organizationID ?? group?.organizationID as number} 
                                            organizationID={state.organizationID ?? -1 } 
                                            onSelected={onOrganizationChanged}
                                            includeAllOption={true}
                                            />


                                        <SideSheetSwitch
                                            label={t("setpointAdmin.viewSelected", {name: getObjectTypeName(eLynxObjectTypeEnum.Recipient)})}
                                            checked={state.viewSelected}
                                            onChange={(checked: boolean) => {
                                                onViewSelected(checked);
                                            }}
                                            disabled={!enableViewSelected} />

                                        <SideSheetFilterInput
                                            label={t("setpointAdmin.filterByObject", {objectType: getObjectTypeName(eLynxObjectTypeEnum.Recipient)})}
                                            value={state.recipientFilterText}
                                            onChange={(value) => {
                                                onSetFilter(value);
                                            }} 
                                            disabled={state.viewSelected}/>


                                        <SideSheetRadioGroup
                                            label={t('setpointAdmin.recipientType')}
                                            value={state.recipientType}
                                            radioButtons={!allowVoiceAlarm ? radioButtons.filter(o => o.value !== RecipientTypeEnum.Voice) : radioButtons}
                                            onChange={(event) => onRecipientTypeChanged(+event.target.value)}
                                            disabled={values?.recipientType !== undefined}
                                        />



                                

                            </SideSheet>

                        </Stack>
                    </Form>
                </LoadingZone>
            </FullScreenDialog>
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getCancelConfirmation}
            />
        </>
    );
}

export type NotificationListEditorDialogState = DialogState<NotificationListEditorDialogInitValues, NotificationListEditorDialogResult>;
export type BindNotificationListEditorDialogReturn = BindDialogReturn<NotificationListEditorDialogInitValues, NotificationListEditorDialogResult>;

export function useNotificationListEditorDialogState(): NotificationListEditorDialogState {
    return useDialogState();
}

export function bindNotificationListEditorDialog(props: NotificationListEditorDialogState): BindNotificationListEditorDialogReturn {
    return bindDialog(props);
}
